// The org chart editor's model (M1-08; spec 06 §Main screens 7, 04 §Org `org-chart`). Pure, so
// the rules are tested without a screen:
//   - the draft is a list of operations (re-parent a seat, add, remove or re-policy an
//     authority or delegation line) applied on top of the chart as last read, so a refetch
//     under an open draft keeps the user's moves and picks up everyone else's;
//   - the `PUT edges` body replaces only the kinds the draft touches (last writer wins per kind,
//     M1-04), and its `reports_to` lines are built from `seats[].supervisor_seat_id`, the
//     canonical reporting line (a seat created by seats.create has no `reports_to` row);
//   - scope (a team, from the TEAMS tree) is a client filter over the chart, never a permission
//     (workspace-modes note §6): the server already filtered what the caller may read;
//   - the team-shape rule (KPI Beast's `validateTeamPlacement`) is advice, not a refusal: the API
//     does not enforce it (members are added one at a time).
import { validateTeamPlacement } from "@companyos/domain-work/org";
import type { OperationInput, OperationOutput } from "@companyos/sdk";

export type OrgChart = OperationOutput<"orgChart.get">;
export type ChartSeat = OrgChart["seats"][number];
export type ChartEdge = OrgChart["edges"][number];
export type ChartTeam = OrgChart["teams"][number];
export type EdgesBody = OperationInput<"orgChart.putEdges">["body"];
export type Simulation = OperationOutput<"orgChart.simulate">;
export type EdgeKind = ChartEdge["kind"];
export type EdgePolicy = ChartEdge["policy"];
/** The two kinds drawn as lines between seats (reporting is the tree itself). */
export type LineKind = Exclude<EdgeKind, "reports_to">;

export const EDGE_KINDS: readonly EdgeKind[] = ["reports_to", "authority", "delegation"];
export const LINE_KINDS: readonly LineKind[] = ["authority", "delegation"];

export const EDGE_LABELS: Record<EdgeKind, string> = {
  reports_to: "Reports to",
  authority: "Authority",
  delegation: "Delegation",
};

/** What a line from a seat means, read from the seat it starts at. */
export const LINE_VERBS: Record<LineKind, string> = {
  authority: "Approves for",
  delegation: "Hands work to",
};

export type DraftOp =
  | { op: "reparent"; seat: string; to: string | null }
  | { op: "add"; kind: LineKind; from: string; to: string; policy?: EdgePolicy }
  | { op: "remove"; kind: LineKind; from: string; to: string }
  | { op: "policy"; kind: LineKind; from: string; to: string; policy: EdgePolicy };

export type Line = { kind: LineKind; from: string; to: string; policy: EdgePolicy };

const lineKey = (l: { kind: string; from: string; to: string }) => `${l.kind}:${l.from}>${l.to}`;

/** Each seat's supervisor with the draft's moves applied. */
export function supervisors(chart: OrgChart, ops: readonly DraftOp[]): Map<string, string | null> {
  const out = new Map(chart.seats.map((s) => [s.id, s.supervisor_seat_id]));
  for (const o of ops) if (o.op === "reparent" && out.has(o.seat)) out.set(o.seat, o.to);
  return out;
}

/** The chart's authority and delegation lines with the draft applied. */
export function linesOf(chart: OrgChart, ops: readonly DraftOp[]): Line[] {
  const out = new Map<string, Line>();
  for (const e of chart.edges)
    if (e.kind !== "reports_to")
      out.set(lineKey({ kind: e.kind, from: e.from_seat_id, to: e.to_seat_id }), {
        kind: e.kind,
        from: e.from_seat_id,
        to: e.to_seat_id,
        policy: e.policy,
      });
  for (const o of ops) {
    if (o.op === "reparent") continue;
    const key = lineKey(o);
    if (o.op === "add")
      out.set(key, { kind: o.kind, from: o.from, to: o.to, policy: o.policy ?? {} });
    else if (o.op === "remove") out.delete(key);
    else {
      const line = out.get(key);
      if (line) out.set(key, { ...line, policy: o.policy });
    }
  }
  return [...out.values()];
}

/** The kinds a draft changes: the body replaces these and leaves the others alone. */
export function draftKinds(ops: readonly DraftOp[]): EdgeKind[] {
  const kinds = new Set<EdgeKind>(ops.map((o) => (o.op === "reparent" ? "reports_to" : o.kind)));
  return EDGE_KINDS.filter((k) => kinds.has(k));
}

/** The `PUT edges` / `simulate` body for a draft: every edge of each kind it touches. */
export function bodyFor(chart: OrgChart, ops: readonly DraftOp[]): EdgesBody {
  const kinds = draftKinds(ops);
  const edges: EdgesBody["edges"] = [];
  if (kinds.includes("reports_to"))
    for (const [seat, to] of supervisors(chart, ops))
      if (to) edges.push({ from_seat_id: seat, to_seat_id: to, kind: "reports_to" });
  for (const l of linesOf(chart, ops))
    if (kinds.includes(l.kind))
      edges.push({
        from_seat_id: l.from,
        to_seat_id: l.to,
        kind: l.kind,
        ...(Object.keys(l.policy).length > 0 ? { policy: l.policy } : {}),
      });
  return { kinds, edges };
}

/**
 * Adds an operation to the draft, folding it into what is there: a seat's later move replaces
 * its earlier one and a move back to where the chart has it drops out; removing a line the draft
 * added drops both; a policy change to a line the draft added edits that add.
 */
export function withOp(chart: OrgChart, ops: readonly DraftOp[], op: DraftOp): DraftOp[] {
  if (op.op === "reparent") {
    const rest = ops.filter((o) => !(o.op === "reparent" && o.seat === op.seat));
    const original = chart.seats.find((s) => s.id === op.seat)?.supervisor_seat_id ?? null;
    return original === op.to ? rest : [...rest, op];
  }
  const key = lineKey(op);
  const same = (o: DraftOp) => o.op !== "reparent" && lineKey(o) === key;
  const stored = chart.edges.some(
    (e) => lineKey({ kind: e.kind, from: e.from_seat_id, to: e.to_seat_id }) === key,
  );
  const added = ops.find((o) => o.op === "add" && same(o));
  if (op.op === "remove") {
    const rest = ops.filter((o) => !same(o));
    return stored ? [...rest, op] : rest;
  }
  if (op.op === "policy" && added?.op === "add")
    return ops.map((o) => (o === added ? { ...added, policy: op.policy } : o));
  return [...ops.filter((o) => !same(o)), op];
}

/** The seats under `seatId` (reports, their reports…) in a supervisor map, cycle-safe. */
export function descendants(seatId: string, sup: ReadonlyMap<string, string | null>): Set<string> {
  const kids = new Map<string, string[]>();
  for (const [s, p] of sup) if (p) kids.set(p, [...(kids.get(p) ?? []), s]);
  const out = new Set<string>();
  const pending = [...(kids.get(seatId) ?? [])];
  while (pending.length > 0) {
    const id = pending.pop() as string;
    if (out.has(id) || id === seatId) continue;
    out.add(id);
    pending.push(...(kids.get(id) ?? []));
  }
  return out;
}

/**
 * Why `dragged` cannot be dropped on `target` as a `kind` edge, or null when it can. The client
 * check only spares a round trip; `simulate` is the judge (the supervisor rule, closed seats).
 */
export function dropRefusal(
  chart: OrgChart,
  ops: readonly DraftOp[],
  kind: EdgeKind,
  dragged: string,
  target: string | null,
): string | null {
  if (dragged === target) return "A seat cannot point at itself.";
  if (kind === "reports_to") {
    const sup = supervisors(chart, ops);
    if ((sup.get(dragged) ?? null) === target) return "It already reports there.";
    if (target && descendants(dragged, sup).has(target))
      return "That seat reports to this one: the line would go round in a circle.";
    return null;
  }
  if (target === null) return "Drop it on a seat.";
  if (linesOf(chart, ops).some((l) => l.kind === kind && l.from === dragged && l.to === target))
    return "That line is already drawn.";
  return null;
}

/** The draft as an operation list for a drop (re-parent or a new line). */
export function dropOp(kind: EdgeKind, dragged: string, target: string | null): DraftOp {
  return kind === "reports_to"
    ? { op: "reparent", seat: dragged, to: target }
    : { op: "add", kind, from: dragged, to: target as string };
}

// Seats as a tree ---------------------------------------------------------------------------------

export type SeatNode<S extends { id: string; title: string } = ChartSeat> = {
  seat: S;
  /** The line reports (with `staffOf`, the staff seats are in `staff` instead). */
  children: SeatNode<S>[];
  /** CHART-STAFF: the staff (assistant) seats drawn at the side of this card; empty by default. */
  staff: SeatNode<S>[];
  /**
   * SHARED-DES: shared DEs that serve this seat (or the team it leads), drawn as linked cards
   * after the line reports and staff. Never a report: `underOf` and every roll-up skip them.
   */
  linked: LinkedNode[];
};

/** A shared DE's linked card under a served seat; its numbers stay on the home card. */
export type LinkedNode = {
  /** The DE's home seat. */
  seatId: string;
  /** The tree node of the home seat (nodes are keyed by seat id). */
  homeNodeId: string;
  linkId: string;
};

/** A serves link as the org chart carries it (`service_links`). */
export type ServiceLink = {
  id: string;
  seat_id: string;
  target_kind: "seat" | "team" | "company";
  target_id: string | null;
};

/** Every seat under a node: its staff and line reports, theirs, and so on. */
export function underOf<S extends { id: string; title: string }>(node: SeatNode<S>): SeatNode<S>[] {
  return [...node.staff, ...node.children].flatMap((n) => [n, ...underOf(n)]);
}

// Sibling order (CHART-TABLE-ORDER) -----------------------------------------------------------------

type Ordered = { id: string; title: string; sort_order?: number | null };

/**
 * The order of seats under one supervisor, shared by the chart (left to right) and the table (top
 * down): the place a manager set (`sort_order`), then the never-ordered ones, titles A–Z.
 */
export function bySiblingOrder(a: Ordered, b: Ordered): number {
  const pa = a.sort_order ?? Number.POSITIVE_INFINITY;
  const pb = b.sort_order ?? Number.POSITIVE_INFINITY;
  if (pa !== pb) return pa < pb ? -1 : 1;
  return a.title.localeCompare(b.title) || a.id.localeCompare(b.id);
}

/** Where a dragged row lands relative to the row it is dropped on. */
export type DropPlace = "before" | "after" | "inside";

/**
 * The drop zone for a pointer at `y` (0 at the row's top) in a row `height` tall: the top and
 * bottom quarters place the row before / after a sibling; the middle makes it a report. A row
 * that is not a sibling of the dragged one can only be "inside".
 */
export function dropPlaceAt(y: number, height: number, sibling: boolean): DropPlace {
  if (!sibling || height <= 0) return "inside";
  if (y < height / 4) return "before";
  if (y > (height * 3) / 4) return "after";
  return "inside";
}

/** `ids` (one supervisor's seats, in order) with `moved` put before or after `target`. */
export function reorderIds(
  ids: readonly string[],
  moved: string,
  target: string,
  place: "before" | "after",
): string[] {
  if (moved === target || !ids.includes(moved) || !ids.includes(target)) return [...ids];
  const rest = ids.filter((id) => id !== moved);
  const at = rest.indexOf(target) + (place === "after" ? 1 : 0);
  return [...rest.slice(0, at), moved, ...rest.slice(at)];
}

/** `ids` with `id` moved one place up (-1) or down (+1); unchanged at either end. */
export function stepIds(ids: readonly string[], id: string, delta: -1 | 1): string[] {
  const i = ids.indexOf(id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= ids.length) return [...ids];
  const out = [...ids];
  [out[i], out[j]] = [out[j] as string, out[i] as string];
  return out;
}

/**
 * The seats reporting to `parent` as stored (the draft's moves left out: `PUT org-chart/order`
 * only orders seats already there), in sibling order, closed seats left out.
 */
export function storedSiblings(chart: OrgChart, parent: string | null): ChartSeat[] {
  return chart.seats
    .filter((s) => s.supervisor_seat_id === parent && s.status !== "closed")
    .sort(bySiblingOrder);
}

/** The chart with `ids` (one supervisor's seats) given places 0…n (the optimistic write). */
export function withOrder(chart: OrgChart, ids: readonly string[]): OrgChart {
  const place = new Map(ids.map((id, i) => [id, i]));
  return {
    ...chart,
    seats: chart.seats.map((s) =>
      place.has(s.id) ? { ...s, sort_order: place.get(s.id) ?? null } : s,
    ),
  };
}

/**
 * Seats as a forest under their supervisors, in sibling order at every level (CHART-TABLE-ORDER:
 * the place a manager set, then titles A–Z). A seat whose supervisor
 * is not among `seats` (closed, out of scope, or on a page not loaded) is a root, so nothing is
 * dropped; a cycle cannot come from the API (the supervisor rule), but a seat is placed once.
 * CHART-STAFF: with `staffOf`, a staff seat under a shown supervisor goes to that node's `staff`
 * (drawn at the side of its card) instead of `children`; it still reports there. Without it,
 * every report is a child (the sidebar's seat tree).
 */
export function seatTree<
  S extends {
    id: string;
    title: string;
    supervisor_seat_id: string | null;
    sort_order?: number | null;
  },
>(
  seats: readonly S[],
  sup?: ReadonlyMap<string, string | null>,
  opts?: {
    staffOf?: (seat: S) => boolean;
    /** SHARED-DES: serves links to draw as linked cards (with `teams` to find a team's lead). */
    links?: readonly ServiceLink[];
    teams?: readonly { id: string; lead_seat_id?: string | null }[];
    /**
     * SHARED-DES-2: every seat of the chart, when `seats` is a slice (a team filter, my line, a
     * search): a home seat outside the slice still gets its linked cards in it, and the ancestor
     * rule reads the whole chart.
     */
    all?: readonly S[];
  },
): SeatNode<S>[] {
  const parentOf = (s: S) => (sup ? (sup.get(s.id) ?? null) : s.supervisor_seat_id);
  const byId = new Map(seats.map((s) => [s.id, s]));
  const kids = new Map<string, S[]>();
  const roots: S[] = [];
  for (const s of seats) {
    const parent = parentOf(s);
    if (parent && parent !== s.id && byId.has(parent)) {
      kids.set(parent, [...(kids.get(parent) ?? []), s]);
    } else roots.push(s);
  }
  const placed = new Set<string>();
  const order = (list: S[]) => [...list].sort(bySiblingOrder);
  const isStaff = (s: S) => opts?.staffOf?.(s) === true;
  const build = (s: S): SeatNode<S> => {
    placed.add(s.id);
    const under = order(kids.get(s.id) ?? []);
    const grow = (list: S[]) => list.filter((c) => !placed.has(c.id)).map(build);
    // Staff first, so a staff seat is placed beside its supervisor before any deeper path.
    const staff = grow(under.filter(isStaff));
    return { seat: s, staff, children: grow(under.filter((c) => !isStaff(c))), linked: [] };
  };
  const forest = order(roots).map(build);
  // Seats only reachable through a cycle: shown as roots rather than lost.
  for (const s of order(seats.filter((x) => !placed.has(x.id))))
    if (!placed.has(s.id)) forest.push(build(s));
  if (opts?.links?.length) {
    const every = opts.all ? new Map(opts.all.map((s) => [s.id, s])) : byId;
    for (const [id, s] of byId) if (!every.has(id)) every.set(id, s);
    placeLinks(forest, opts.links, opts.teams ?? [], parentOf, every);
  }
  return forest;
}

/**
 * SHARED-DES: hangs each serves link under the served seat (a team: its lead seat; the company:
 * nowhere). The ancestor rule: never under the home seat, a seat above it (the home chain sees it
 * already) or a seat in its own branch. One linked card per served seat and home.
 * SHARED-DES-2: the home seat need not be drawn (a team filter keeps the served team's shared
 * cards); `byId` holds the whole chart, so both rules read the real lines, not the slice.
 */
function placeLinks<S extends { id: string; title: string }>(
  forest: SeatNode<S>[],
  links: readonly ServiceLink[],
  teams: readonly { id: string; lead_seat_id?: string | null }[],
  parentOf: (s: S) => string | null,
  byId: ReadonlyMap<string, S>,
): void {
  const nodes = new Map<string, SeatNode<S>>();
  const index = (n: SeatNode<S>) => {
    nodes.set(n.seat.id, n);
    for (const c of [...n.staff, ...n.children]) index(c);
  };
  for (const n of forest) index(n);
  const lead = new Map(teams.map((t) => [t.id, t.lead_seat_id ?? null]));
  /** The seats above `id` on the whole chart (its line up). */
  const chainOf = (id: string) => {
    const above = new Set<string>();
    for (let s = byId.get(id); s; ) {
      const p = parentOf(s);
      if (!p || above.has(p)) break;
      above.add(p);
      s = byId.get(p);
    }
    return above;
  };
  for (const link of links) {
    const home = link.seat_id;
    if (!byId.has(home) || link.target_kind === "company" || !link.target_id) continue;
    const at = link.target_kind === "team" ? lead.get(link.target_id) : link.target_id;
    const target = at ? nodes.get(at) : undefined;
    if (!target || target.seat.id === home) continue;
    // Above the home seat, or in its own branch (the home seat above the target)?
    if (chainOf(home).has(target.seat.id)) continue;
    if (chainOf(target.seat.id).has(home)) continue;
    if (target.linked.some((l) => l.seatId === home)) continue;
    target.linked.push({ seatId: home, homeNodeId: home, linkId: link.id });
  }
}

// Holders, composition, teams ---------------------------------------------------------------------

export type SeatComposition = "human" | "mixed" | "digital" | "open";

/** A seat's mix of holders (02 §team `composition`, per seat): nobody on it is `open`. */
export function seatComposition(seat: Pick<ChartSeat, "holders">): SeatComposition {
  const person = seat.holders.some((h) => h.membership_kind === "person");
  const digital = seat.holders.some((h) => h.membership_kind === "digital_employee");
  return digital ? (person ? "mixed" : "digital") : person ? "human" : "open";
}

export const COMPOSITION_LABELS: Record<SeatComposition, string> = {
  human: "Human",
  mixed: "Mixed",
  digital: "Digital",
  open: "Open",
};

export const COMPOSITION_TONE: Record<SeatComposition, "neutral" | "accent" | "info" | "outline"> =
  { human: "neutral", mixed: "info", digital: "accent", open: "outline" };

export type TeamTreeNode<T extends { id: string; name: string; parent_team_id: string | null }> = {
  team: T;
  children: TeamTreeNode<T>[];
};

/** Teams under their parents, A–Z; a team whose parent is not visible is a root. */
export function teamTree<T extends { id: string; name: string; parent_team_id: string | null }>(
  teams: readonly T[],
): TeamTreeNode<T>[] {
  return seatTree(
    teams.map((t) => ({ ...t, title: t.name, supervisor_seat_id: t.parent_team_id })),
  ).map(function back(n): TeamTreeNode<T> {
    const { title: _t, supervisor_seat_id: _s, ...team } = n.seat;
    return { team: team as unknown as T, children: n.children.map(back) };
  });
}

/** The member seats of a team and of every team under it (the scope filter). */
export function teamSeats(
  chart: Pick<OrgChart, "teams" | "team_members">,
  teamId: string,
): Set<string> {
  const ids = new Set([teamId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const t of chart.teams)
      if (t.parent_team_id && ids.has(t.parent_team_id) && !ids.has(t.id)) {
        ids.add(t.id);
        grew = true;
      }
  }
  return new Set(chart.team_members.filter((m) => ids.has(m.team_id)).map((m) => m.seat_id));
}

/** The team a scope value names (`team:{id}`), or null. */
export function scopeTeam(scope: string): string | null {
  return scope.startsWith("team:") ? scope.slice(5) : null;
}

/**
 * Advice on a team's shape (KPI Beast `validateTeamPlacement`): a team is either a manager's
 * whole reporting branch, or peers who report to the same manager (the lead may sit above
 * them). Null when the team fits either; otherwise the peer rule's message. Never a refusal.
 */
export function teamShapeAdvice(
  team: { lead_seat_id: string | null },
  memberIds: readonly string[],
  sup: ReadonlyMap<string, string | null>,
): string | null {
  if (memberIds.length === 0) return null;
  const seats = [...sup].map(([id, parent]) => ({ id, parentSeatId: parent }));
  const branch =
    team.lead_seat_id !== null &&
    validateTeamPlacement(
      {
        kind: "team",
        membershipMode: "branch",
        leadSeatId: team.lead_seat_id,
        memberIds: [...memberIds],
      },
      seats,
    ) === null;
  if (branch) return null;
  const peers = memberIds.filter((id) => id !== team.lead_seat_id);
  if (peers.length === 0) return null;
  return validateTeamPlacement(
    { kind: "team", membershipMode: "explicit", leadSeatId: null, memberIds: peers },
    seats,
  );
}

/** The teams whose shape advice a draft changes from fine to not (shown in the review). */
export function adviceAfter(
  chart: OrgChart,
  ops: readonly DraftOp[],
): { team: ChartTeam; advice: string }[] {
  const before = supervisors(chart, []);
  const after = supervisors(chart, ops);
  return chart.teams.flatMap((team) => {
    const members = chart.team_members.filter((m) => m.team_id === team.id).map((m) => m.seat_id);
    const now = teamShapeAdvice(team, members, after);
    return now && !teamShapeAdvice(team, members, before) ? [{ team, advice: now }] : [];
  });
}
